/**
 * Subscription Service
 *
 * API service for subscription plan management operations.
 */

import { apiClient } from '../axios';
import { API_ENDPOINTS } from '../endpoints';
import type {
  ApiResponse,
  ListRequest,
  ListResponse,
  SubscriptionPlanWithTerms,
  PlanUpgradeRequest,
  PlanUpgradeResponse,
  CreateSubscriptionPlanRequest,
  UpdateSubscriptionPlanRequest,
  AddSubscriptionPlanTermRequest,
  UpdateSubscriptionPlanTermRequest,
} from '../types';

// ============================================
// Subscription Plan List
// ============================================

/**
 * Get list of subscription plans with pagination
 */
// export async function getSubscriptionPlanList(
//   params: ListRequest
// ): Promise<SubscriptionPlanListResponse> {
//   const response = await apiClient.post<ApiResponse<SubscriptionPlanListResponse>>(
//     API_ENDPOINTS.SUBSCRIPTION.PLAN_LIST,
//     params
//   );
//   return response.data.result;
// }
export interface PlanListApiItem {
  subscriptionPlan: {
    id: string;
    code: string;
    name: string;
    status: string;
    description: string;
    maxUsers: number;
    maxContracts: number;
    maxActiveContracts: number;
    maxEmployees: number;
    totalSmsCredits: number;
    customReportTemplatesEnabled: boolean;
    eInvoiceEnabled: boolean;
    smsFeatureEnabled: boolean;
    mobileAppFeatureEnabled: boolean;
    isCustomPlan: boolean;
    orderNumber: number;
    isActive: boolean;
  };
  planTerms: {
    id: string;
    subscriptionPlanId: string;
    planTermCode: string;
    termType: string;
    termValue: number;
    price: number;
    actualPrice: number;
    isActive: boolean;
  }[];
}

export async function getSubscriptionPlanList(): Promise<PlanListApiItem[]> {
  const response = await apiClient.get<ApiResponse<PlanListApiItem[]>>(
    API_ENDPOINTS.SUBSCRIPTION.PLAN_LIST
  );
  return response.data.result;
}

/**
 * Get paginated subscription plan grid list (with terms).
 * POST /api/payroll-platform/org-plan/subscription-plan-grid-list
 */
export async function getSubscriptionPlanGridList(
  params: ListRequest
): Promise<ListResponse<PlanListApiItem>> {
  const response = await apiClient.post<ApiResponse<ListResponse<PlanListApiItem>>>(
    API_ENDPOINTS.SUBSCRIPTION.PLAN_GRID_LIST,
    params
  );
  return response.data.result;
}
/**
 * Get list of subscription plans with terms (new endpoint)
 */
export async function getSubscriptionPlanListNew(
  params: ListRequest
): Promise<SubscriptionPlanWithTerms[]> {
  const response = await apiClient.post<ApiResponse<SubscriptionPlanWithTerms[]>>(
    API_ENDPOINTS.SUBSCRIPTION.PLAN_LIST_NEW,
    params
  );
  return response.data.result;
}

// ============================================
// Allowed Plans
// ============================================

/**
 * Get allowed plans for a user by userId
 */
export async function getAllowedPlansByUserId(userId: string): Promise<PlanListApiItem[]> {
  const response = await apiClient.get<ApiResponse<PlanListApiItem[]>>(
    API_ENDPOINTS.ORG_REGISTRATION.ALLOWED_PLAN_DETAILS_BY_USER(userId)
  );
  return response.data.result;
}

// ============================================
// Plan Terms by Plan ID
// ============================================

export type PlanTerm = PlanListApiItem['planTerms'][number];

/**
 * Get plan terms by subscription plan ID (used for renew)
 */
export async function getPlanTermsByPlanId(planId: string): Promise<PlanTerm[]> {
  const response = await apiClient.get<ApiResponse<PlanTerm[] | Record<string, unknown>>>(
    API_ENDPOINTS.SUBSCRIPTION.GET_PLAN_TERMS_BY_PLAN_ID(planId)
  );
  const result = response.data.result;
  if (Array.isArray(result)) return result;
  // If the API wraps terms inside an object, extract the array
  if (result && typeof result === 'object') {
    const arr = Object.values(result).find(Array.isArray);
    return (arr as PlanTerm[]) ?? [];
  }
  return [];
}

// ============================================
// Create Subscription Plan
// ============================================

/**
 * Create a new subscription plan
 */
export async function createSubscriptionPlan(
  data: CreateSubscriptionPlanRequest
): Promise<unknown> {
  const response = await apiClient.post<ApiResponse<unknown>>(
    API_ENDPOINTS.SUBSCRIPTION.CREATE_SUBSCRIPTION_PLAN,
    data
  );
  return response.data.result;
}

/**
 * Update an existing subscription plan
 */
export async function updateSubscriptionPlan(
  data: UpdateSubscriptionPlanRequest
): Promise<unknown> {
  const response = await apiClient.post<ApiResponse<unknown>>(
    API_ENDPOINTS.SUBSCRIPTION.UPDATE_SUBSCRIPTION_PLAN,
    data
  );
  return response.data.result;
}

// ============================================
// Add Subscription Plan Term
// ============================================

/**
 * Add a term to a subscription plan
 */
export async function addSubscriptionPlanTerm(
  data: AddSubscriptionPlanTermRequest
): Promise<unknown> {
  const response = await apiClient.post<ApiResponse<unknown>>(
    API_ENDPOINTS.SUBSCRIPTION.ADD_SUBSCRIPTION_PLAN_TERM,
    data
  );
  return response.data.result;
}

/**
 * Update an existing subscription plan term
 */
export async function updateSubscriptionPlanTerm(
  data: UpdateSubscriptionPlanTermRequest
): Promise<unknown> {
  const response = await apiClient.post<ApiResponse<unknown>>(
    API_ENDPOINTS.SUBSCRIPTION.UPDATE_SUBSCRIPTION_PLAN_TERM,
    data
  );
  return response.data.result;
}

// ============================================
// Plan Upgrade
// ============================================

/**
 * Upgrade organization's subscription plan
 */
export async function upgradeSubscriptionPlan(
  data: PlanUpgradeRequest
): Promise<PlanUpgradeResponse> {
  const response = await apiClient.post<ApiResponse<PlanUpgradeResponse>>(
    API_ENDPOINTS.SUBSCRIPTION.UPGRADE,
    data
  );
  return response.data.result;
}

// ============================================
// Export all functions
// ============================================

export const subscriptionService = {
  getSubscriptionPlanList,
  getSubscriptionPlanGridList,
  getSubscriptionPlanListNew,
  createSubscriptionPlan,
  updateSubscriptionPlan,
  addSubscriptionPlanTerm,
  updateSubscriptionPlanTerm,
  upgradeSubscriptionPlan,
  getAllowedPlansByUserId,
  getPlanTermsByPlanId,
};
